import { KlevuConfig, } from "../index.js";
import { KlevuLastClickedProducts } from "../store/lastClickedProducts.js";
import { KlevuLastSearches } from "../store/lastSearches.js";
import { KlevuEventV1CategoryProductClick, KlevuEventV1CategoryView, KlevuEventV1CheckedOutProducts, KlevuEventV1ProductTracking, KlevuEventV1Search, KlevuEventV2, } from "./eventRequests.js";
/**
 * @category KlevuEvents
 */
export class KlevuEvents {
    /**
     * Tell Klevu what products where bought by the user
     *
     * @param items Items user bought
     * @property amount count of bought products
     * @property product KlevuProduct that is being bought
     * @property variantId optional variantId that is being bought
     * @property override optional override any settings of sent data
     *
     */
    static buy({ items, }) {
        var _a, _b;
        for (const item of items) {
            const p = item.product;
            if (!p.id) {
                throw new Error("Cannot send event without product id");
            }
            const data = {
                klevu_apiKey: KlevuConfig.getDefault().apiKey,
                klevu_currency: (_a = p.currency) !== null && _a !== void 0 ? _a : "usd",
                klevu_productGroupId: p.itemGroupId || p.id,
                klevu_productId: p.id,
                klevu_salePrice: parseFloat((_b = p.salePrice) !== null && _b !== void 0 ? _b : "0"),
                klevu_productVariantId: item.variantId || p.id,
                klevu_type: "checkout",
                klevu_unit: item.amount,
            };
            KlevuEventV1CheckedOutProducts(Object.assign(Object.assign({}, data), item.override));
        }
    }
    /**
     * When recommendation banner is shown in the page
     *
     * @param recommendation Metadata of what recommendation is shown
     * @param products List of all products that are shown
     * @param override Ability to override any analytical keys in low level
     */
    static recommendationView({ recommendationMetadata, products, override = {}, }) {
        const data = {
            event: "view_recs_list",
            event_apikey: KlevuConfig.getDefault().apiKey,
            event_list_id: recommendationMetadata.recsKey,
            event_list_logic: recommendationMetadata.logic,
            event_list_name: recommendationMetadata.title,
            items: products.map((p, index) => {
                var _a, _b;
                if (!p.id) {
                    throw new Error("Cannot send event without product id");
                }
                return {
                    index: index + 1,
                    item_id: p.id,
                    item_group_id: p.itemGroupId || p.id,
                    item_name: (_a = p.name) !== null && _a !== void 0 ? _a : "unknown",
                    item_variant_id: p.itemGroupId || p.id,
                    price: (_b = p.price) !== null && _b !== void 0 ? _b : "0",
                    currency: p.currency,
                    item_brand: p.brand,
                    item_category: p.category,
                };
            }),
        };
        KlevuEventV2([
            Object.assign(Object.assign({}, data), override),
        ]);
    }
    /**
     * When product has been clicked in the recommendation banner
     *
     * @param recommendationMetadata Metadata of what recommendation is clicked
     * @param product Which product is clicked in the list
     * @param productIndexInList What is the index of the product in the list. Starting from 1
     * @param override Ability to override any analytical keys in low level
     */
    static recommendationClick({ recommendationMetadata, product, productIndexInList, variantId, override = {}, }) {
        var _a, _b;
        if (!product.id) {
            throw new Error("Cannot send event without product id");
        }
        KlevuLastClickedProducts.click(product.id, product);
        const data = {
            event: "select_recs_list",
            event_apikey: KlevuConfig.getDefault().apiKey,
            event_list_id: recommendationMetadata.recsKey,
            event_list_logic: recommendationMetadata.logic,
            event_list_name: recommendationMetadata.title,
            items: [
                {
                    index: productIndexInList,
                    item_id: product.id,
                    item_group_id: product.itemGroupId || product.id,
                    item_name: (_a = product.name) !== null && _a !== void 0 ? _a : "unknown",
                    item_variant_id: variantId || product.id,
                    price: (_b = product.price) !== null && _b !== void 0 ? _b : "0",
                    currency: product.currency,
                    item_brand: product.brand,
                    item_category: product.category,
                },
            ],
        };
        KlevuEventV2([
            Object.assign(Object.assign({}, data), override),
        ]);
    }
    /**
     * When product is clicked. Do not use this for recommendations
     *
     * @param searchTerm
     * @param product
     */
    static searchProductClick({ product, searchTerm, variantId, activeFilters, override = {}, }) {
        var _a, _b;
        if (!product.id) {
            throw new Error("Cannot send event without product id");
        }
        KlevuLastClickedProducts.click(product.id, product);
        const data = {
            klevu_apiKey: KlevuConfig.getDefault().apiKey,
            klevu_type: "clicked",
            klevu_keywords: searchTerm,
            klevu_productGroupId: product.itemGroupId || product.id,
            klevu_productId: product.id,
            klevu_productName: (_a = product.name) !== null && _a !== void 0 ? _a : "unknown",
            klevu_productUrl: (_b = product.url) !== null && _b !== void 0 ? _b : "",
            klevu_productVariantId: variantId || product.id,
            klevu_activeFilters: activeFilters,
        };
        KlevuEventV1ProductTracking(Object.assign(Object.assign({}, data), override));
    }
    /**
     * What user has last searched. This is important for Klevu to function
     * properly. Use `sendSearchEvent()` modifier with search query to send results
     *
     * @param term What was searched
     * @param totalResults Total number of results (can be found in result meta)
     * @param typeOfSearch Type of search used (can be found in result meta)
     * @param override Ability to override any analytical keys in low level
     */
    static search({ term, totalResults, typeOfSearch, activeFilters, override = {}, }) {
        KlevuLastSearches.save(term);
        const data = {
            klevu_apiKey: KlevuConfig.getDefault().apiKey,
            klevu_term: term,
            klevu_totalResults: totalResults,
            klevu_typeOfQuery: typeOfSearch,
            klevu_activeFilters: activeFilters,
        };
        KlevuEventV1Search(Object.assign(Object.assign({}, data), override));
    }
    /**
     *
     * @param categoryTitle This is the name of the category being visited. For example, Stackable Rings. The name should not include parent categories.
     * @param klevuCategory This is the complete hierarchy of the category being visited. For example, Jewellery;Rings;Stackable Rings. Please note the use of a semicolon as the separator between a parent and a child category.
     * @param products Products in the view
     * @param pageStartsFrom Offset of the first product being shown on this page. For example, if you are displaying 30 products per page and if a customer is on the 2nd page, the value here should be 30. If on the 3rd page, it will be 60.
     * @param abTestId The AB test id currently running
     * @param abTestVariantId Id of AB test variant
     * @param activeFilters The string version of active filters applied to the query that got the products.
     * @param override Ability to override any analytical keys in low level
     */
    static categoryMerchandisingView({ categoryTitle, klevuCategory, products, pageStartsFrom, abTestId, abTestVariantId, activeFilters, override = {}, }) {
        const data = {
            klevu_apiKey: KlevuConfig.getDefault().apiKey,
            klevu_categoryName: categoryTitle,
            klevu_categoryPath: klevuCategory,
            klevu_productIds: products.map((p) => p.id).join(","),
            klevu_pageStartsFrom: pageStartsFrom,
            klevu_activeFilters: activeFilters,
            klevu_abTestId: abTestId,
            klevu_abTestVariantId: abTestVariantId,
        };
        KlevuEventV1CategoryView(Object.assign(Object.assign({}, data), override));
    }
    /**
     *
     * @param product Product clicked
     * @param categoryTitle This is the name of the category being visited. For example, Stackable Rings. The name should not include parent categories.
     * @param klevuCategory This is the complete hierarchy of the category being visited. For example, Jewellery;Rings;Stackable Rings. Please note the use of a semicolon as the separator between a parent and a child category.
     * @param variantId This is the child/variant ID of the clicked product. eg. 12345. For compound products with a parent and multiple child/variant products, this is the ID of the specific variant.
     * @param productPosition Position of the product on the category page when it was clicked. For example, the value would be 0 if it is the first product on the first page. The value will be 30, if it is the first product on the 2nd page with 30 products being displayed per page.
     * @param activeFilters The string version of active filters applied to the query that got the products.
     * @param override Ability to override any analytical keys in low level
     */
    static categoryMerchandisingProductClick(product, categoryTitle, klevuCategory, variantId, productPosition, abTestId, abTestVariantId, activeFilters, override = {}) {
        if (!product.id) {
            throw new Error("Cannot send event without product id");
        }
        KlevuLastClickedProducts.click(product.id, product);
        const data = {
            klevu_apiKey: KlevuConfig.getDefault().apiKey,
            klevu_categoryName: categoryTitle,
            klevu_productGroupId: product.itemGroupId || product.id,
            klevu_productVariantId: variantId || product.id,
            klevu_categoryPath: klevuCategory,
            klevu_productId: product.id,
            klevu_productName: product.name,
            klevu_productUrl: product.url,
            klevu_salePrice: product.salePrice,
            klevu_productSku: product.sku,
            klevu_productPosition: productPosition,
            klevu_abTestId: abTestId,
            klevu_abTestVariantId: abTestVariantId,
            klevu_activeFilters: activeFilters,
        };
        KlevuEventV1CategoryProductClick(Object.assign(Object.assign({}, data), override));
    }
}
